// $FlowFixMe
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { pick, isEmpty } from 'ramda';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Card,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';

import messages from './messages';
import EditUnitGeneralInformationFormModal from './EditUnitModal';
import EditUnitGeneralInformationFormModalLegacy from './EditUnitModal/EditUnitModalLegacy';
import ElementWithPermissions from '../../../components/ElementWithPermissions';
import UnitSqFtInformation from '../UnitSqFtInformation';
import useIsAffordableProperty from '../../../hooks/useIsAffordableProperty';
import { showsXmlUnitNumberInGeneralUnitInformation } from '../utils';

import { PH_PROGRAM_NAME } from '../../../containers/GeneralAffordable/constants';

const GeneralUnitInformation = ({
  unit,
  intl,
  selectedProperty,
  refreshUnit,
  isHUDFloorPlan,
  getUnitById,
  flags,
}: Object) => {
  const isAffordableProperty = useIsAffordableProperty(selectedProperty);
  const {
    unitLevelPrice: unitLevelPriceFlag,
    publicHousing,
    removeUnitFloorLevelRestrictions,
    affUnitDetails,
    adaAccessibilityTypes,
  } = useFlags();
  const [modal, setModal] = useState(false);
  const [unitToEdit, setUnitToEdit] = useState({});
  const resetModal = () => setModal(false);
  const noUnitLoaded = Object.keys(unit ?? {}).length === 0;

  // Feature flag logic
  const showUnitQuotingRentField = unitLevelPriceFlag; // Show if flag is on, regardless of availability

  // Market units never render Unit Affordable Setup, so the field lives here.
  const showXmlUnitNumberField = showsXmlUnitNumberInGeneralUnitInformation({
    isAffordableProperty,
    floorPlan: unit?.floorPlan,
    affUnitDetailsEnabled: affUnitDetails,
  });

  const handleEditUnit = () => {
    const unitToEditInitialValues = pick(
      [
        'id',
        'unitFloorLevel',
        'streetAddress1',
        'streetAddress2',
        'state',
        'city',
        'zipCode',
        'isADA',
        // Only when this card owns it; stale values here revert the card's save.
        ...(showXmlUnitNumberField ? ['xmlUnitNumber'] : []),
        'unitStatus', // Add unitStatus to pass availability info
        'quotingRentAmount', // Add quotingRentAmount for unit price field
        'PHUnitNumber',
      ],
      unit,
    );

    const isADA = (unit?.isADA ?? false).toString();

    setUnitToEdit({
      ...unitToEditInitialValues,
      isADA, // Handle null/undefined values
      // Seed the current accessibility type selections (ids) for the multi-select.
      accessibilityTypeIds: (unit?.accessibilityTypes ?? []).map(
        (type) => type.id,
      ),
    });
    setModal(true);
  };

  const unitIsManagementOwned = unit?.unitStatus?.isManagementOwned ?? false;
  const editUnitScopes = unitIsManagementOwned
    ? ['manage-units-update-property-office-or-model']
    : ['manage-units-update'];

  const unitMarketingDescription =
    unit?.floorPlan?.marketingDescription ?? '---';
  const unitGrossSquareFeet = unit?.floorPlan?.grossSquareFeet ?? null;
  const unitGrossSquareFeetFormatted =
    unitGrossSquareFeet !== null
      ? `${unitGrossSquareFeet} ${intl.formatMessage(messages.sqFt)}`
      : '---';

  const unitBaseMarketRentAmount =
    unit?.floorPlan?.baseMarketRentAmount ?? null;

  // Floorplan quoting rent amount (always from floorplan)
  const floorplanQuotingRent = unit?.floorPlan?.quotingRentAmount ?? null;

  const unitEstimatedDepositAmount =
    unit?.floorPlan?.estimatedDepositAmount ?? null;
  const unitFloorLevel = unit?.unitFloorLevel ?? '---';

  const unitAddress = {
    address1: unit.streetAddress1,
    address2: unit?.streetAddress2 ?? null,
    state: unit.state,
    city: unit.city,
    zipCode: unit.zipCode,
  };

  const formatCurrency = (value: number | null) =>
    value !== null
      ? intl.formatNumber(+value, {
          style: 'currency',
          currency: 'USD',
        })
      : '---';

  const isADAUnit = () => {
    if (!unit || isEmpty(unit)) return '---';
    return unit.isADA === true ? 'Yes' : 'No';
  };

  const sqFt = {
    grossSqFt: unit?.floorPlan?.grossSquareFeet ?? '---',
    rentableSqFt: unit?.unitRentableSquareFeet ?? '---',
    usableSqFt: unit?.unitUsableSquareFeet ?? '---',
  };

  const buildingName = unit?.building?.name ?? '---';

  const accessibilityTypesDisplay =
    (unit?.accessibilityTypes ?? []).map((type) => type.name).join(', ') ||
    '---';

  // `||`, not `??` — clearing the field persists an empty string.
  const xmlUnitNumber = unit?.xmlUnitNumber || '---';

  const phUnitNumber = unit?.PHUnitNumber ?? '---';

  const isPHFloorPlan = publicHousing
    ? unit?.floorPlan?.floorPlanAffordablePrograms?.some((fpap) =>
        fpap.propertyAffordableProgram?.masterAffordableProgram?.name?.includes(
          PH_PROGRAM_NAME,
        ),
      )
    : false;

  return (
    <div>
      {adaAccessibilityTypes ? (
        <EditUnitGeneralInformationFormModal
          unitToEdit={unitToEdit}
          onClose={resetModal}
          show={modal === true}
          intl={intl}
          selectedProperty={selectedProperty}
          refreshUnit={refreshUnit}
          getUnitById={getUnitById}
          flags={flags}
          isPHFloorPlan={isPHFloorPlan}
          isHUDFloorPlan={isHUDFloorPlan}
          showXmlUnitNumberField={showXmlUnitNumberField}
        />
      ) : (
        <EditUnitGeneralInformationFormModalLegacy
          unitToEdit={unitToEdit}
          onClose={resetModal}
          show={modal === true}
          intl={intl}
          selectedProperty={selectedProperty}
          refreshUnit={refreshUnit}
          getUnitById={getUnitById}
          flags={flags}
          isPHFloorPlan={isPHFloorPlan}
          isHUDFloorPlan={isHUDFloorPlan}
          showXmlUnitNumberField={showXmlUnitNumberField}
        />
      )}
      <Card sx={{ marginBottom: 2, padding: 2 }}>
        <Grid container spacing={0}>
          {/* Header */}
          <Grid
            item
            xs={12}
            padding={'0px 5px 16px 5px'}
            borderBottom={'1px solid #E0E0E0'}
          >
            <Grid
              container
              spacing={0}
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item xs={3} md={4}>
                <Typography variant="titleLarge">
                  <FormattedMessage {...messages.title} />
                </Typography>
              </Grid>
              <Grid item xs={9} md={8} style={{ textAlign: 'right' }}>
                <ElementWithPermissions scope={editUnitScopes}>
                  {noUnitLoaded ? (
                    <a className="simple-link" disabled>
                      <i className="et-pencil" />
                      <FormattedMessage {...messages.unitEditButton} />
                    </a>
                  ) : (
                    <a className="simple-link" onClick={handleEditUnit}>
                      <i className="et-pencil" />
                      <FormattedMessage {...messages.unitEditButton} />
                    </a>
                  )}
                </ElementWithPermissions>
              </Grid>
            </Grid>
          </Grid>
          {/* Left Content */}
          <Grid item xs={6} padding={'0px 0px 0px 10px'}>
            <Grid container spacing={1} sx={{ marginTop: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography fontWeight="bold">
                  <FormattedMessage {...messages.marketingDescription} />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>{unitMarketingDescription}</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography fontWeight="bold">
                  <FormattedMessage {...messages.grossSqFt} />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>{unitGrossSquareFeetFormatted}</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography fontWeight="bold">
                  <FormattedMessage
                    {...(removeUnitFloorLevelRestrictions
                      ? messages.unitFloorLevel
                      : messages.unitLevel)}
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>{unitFloorLevel}</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography fontWeight="bold">
                  <FormattedMessage {...messages.unitAddress} />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  {unitAddress.address1} {unitAddress.address2}
                </Typography>
                <br />
                <Typography>
                  {unitAddress.city}, {unitAddress.state} {unitAddress.zipCode}
                </Typography>
              </Grid>

              {showXmlUnitNumberField && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography fontWeight="bold">
                      <FormattedMessage {...messages.xmlAltUnitNumber} />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography>{xmlUnitNumber}</Typography>
                  </Grid>
                </>
              )}

              {isPHFloorPlan && !affUnitDetails && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography fontWeight="bold">
                      <FormattedMessage {...messages.phUnitNumber} />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography>{phUnitNumber}</Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
          {/* Right Content */}
          <Grid item xs={6} padding={'0px 0px 0px 10px'}>
            <Grid container spacing={1} sx={{ marginTop: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography fontWeight="bold">
                  <FormattedMessage {...messages.baseMarketRentAmount} />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  {formatCurrency(unitBaseMarketRentAmount)}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography fontWeight="bold">
                  <FormattedMessage {...messages.quotingRentAmount} />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>{formatCurrency(floorplanQuotingRent)}</Typography>
              </Grid>

              {showUnitQuotingRentField && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography fontWeight="bold">
                      <FormattedMessage {...messages.unitQuotingRentAmount} />
                      <Tooltip
                        title={intl.formatMessage(
                          messages.unitQuotingRentAmountTooltip,
                        )}
                        placement="top"
                        arrow
                        style={{
                          display: 'inline-flex',
                          marginLeft: '8px',
                          verticalAlign: 'middle',
                        }}
                      >
                        <AlertInfoIcon fontSize="small" />
                      </Tooltip>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography>
                      {formatCurrency(unit?.quotingRentAmount ?? null)}
                    </Typography>
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={6}>
                <Typography fontWeight="bold">
                  <FormattedMessage {...messages.estimatedDepositAmount} />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  {formatCurrency(unitEstimatedDepositAmount)}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography fontWeight="bold">
                  <FormattedMessage {...messages.ADAUnit} />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>{isADAUnit()}</Typography>
              </Grid>

              {adaAccessibilityTypes && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography fontWeight="bold">
                      <FormattedMessage {...messages.accessibilityTypes} />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography>{accessibilityTypesDisplay}</Typography>
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={6}>
                <Typography fontWeight="bold">
                  <FormattedMessage {...messages.buildingName} />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>{buildingName}</Typography>
              </Grid>
            </Grid>
          </Grid>
          {/* Unit SQ FT Information */}
          <Grid item xs={12} marginTop={'20px'}>
            <UnitSqFtInformation
              intl={intl}
              sqFt={sqFt}
              selectedProperty={selectedProperty}
              unitId={unit.id}
              refreshUnitCallback={getUnitById}
            />
          </Grid>
        </Grid>
      </Card>
    </div>
  );
};

export default GeneralUnitInformation;
